import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { resendVerification, mintMobileAuthCode } from "../api/auth.api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import OAuthButtons from "../components/OAuthButtons";
import {
  markMobileAuthFromSearch,
  isMobileAuthFlow,
  redirectToMobileApp,
} from "../auth/mobileAuth";
import { useTranslation } from "react-i18next";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState(null);
  const [notVerified, setNotVerified] = useState(false);
  const [info, setInfo] = useState(null);

  // 🔒 Account-Lock-States
  const [lockedUntil, setLockedUntil] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const oauthError = location.state?.oauthError;

  // App-Login-Flow (?mobile=1) merken, damit nach dem Login an die App
  // uebergeben werden kann.
  useEffect(() => {
    markMobileAuthFromSearch(location.search);
  }, [location.search]);

  /* =========================
     ⏱️ Countdown-Effekt
  ========================= */
  useEffect(() => {
    if (!lockedUntil) return;

    const interval = setInterval(() => {
      const diff = Math.floor(
        (new Date(lockedUntil) - new Date()) / 1000
      );

      if (diff <= 0) {
        setLockedUntil(null);
        setSecondsLeft(null);
        clearInterval(interval);
      } else {
        setSecondsLeft(diff);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lockedUntil]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError(null);
    setInfo(null);
    setNotVerified(false);
    setLockedUntil(null);
    setSecondsLeft(null);

    try {
      await auth.login(email, password);
      if (isMobileAuthFlow()) {
        const { code } = await mintMobileAuthCode();
        redirectToMobileApp(code);
        return;
      }
      navigate("/dashboard");
    } catch (err) {
      const code = err?.response?.data?.code;
      const until = err?.response?.data?.lockedUntil;

      if (code === "ACCOUNT_LOCKED") {
        setLockedUntil(until);
        setError(t("auth.login.errorLocked"));
        return;
      }

      if (code === "EMAIL_NOT_VERIFIED") {
        setError(t("auth.login.errorNotVerified"));
        setNotVerified(true);
        return;
      }

      setError(t("auth.login.errorInvalidCredentials"));
    }
  };

  const handleResend = async () => {
    try {
      await resendVerification(email);
      setInfo(
        t("auth.login.resendInfo")
      );
      setNotVerified(false);
    } catch {
      setInfo(
        t("auth.login.resendInfo")
      );
    }
  };

  return (
    <div className="content">
    <Header />
    <form className="login-form" onSubmit={handleSubmit}>
      {/*<img className="login-logo" alt="Data optimizer Logo" src="./data-optimizer-logo.png" />*/}
      <h2 className="login-headline">{t("auth.login.title")}</h2>
      

      {error && <p style={{ color: "red" }}>{error}</p>}
      {oauthError && <p style={{ color: "red" }}>{t("auth.oauth.error")}</p>}

      {/* 🔒 Countdown-Anzeige */}
      {secondsLeft !== null && (
        <p style={{ color: "#555" }}>
          {t("auth.login.waitMinutes", { minutes: Math.ceil(secondsLeft / 60) })}
        </p>
      )}

      {info && <p style={{ color: "green" }}>{info}</p>}

      <input
        data-testid="login-email"
        placeholder={t("auth.email")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />

      <input
        data-testid="login-password"
        placeholder={t("auth.password")}
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />

      <button className="form-button"
        data-testid="login-submit"
        type="submit"
        disabled={secondsLeft !== null}
      >
        {t("auth.login.submit")}
      </button>

      <OAuthButtons />

      {/* ✅ NUR wenn Backend EMAIL_NOT_VERIFIED meldet */}
      {notVerified && (
        <div style={{ marginTop: "1rem" }}>
          <button type="button" onClick={handleResend}>
            {t("auth.login.resendVerification")}
          </button>
        </div>
      )}

      <p style={{ marginTop: "1rem" }}>
        <a className="form-subtext" href="/forgot-password">{t("auth.login.forgotPassword")}</a>
      </p>
      <p className="form-subtext">{t("auth.login.noAccount")} <a href="/register">{t("auth.login.registerNow")}</a></p>
    </form>
    <Footer />
    </div>
  );
}